/**
 * One send's summary and the Dispatch log's outcome badges (moved out of
 * `dispatch-rep-status.ts` unchanged — review-fix U14, F1). Every number here
 * is read off the ONE classifier (`dispatch-rep-status.ts#classifyRepStatus`),
 * so the server's summary, the chips and the Retry button cannot disagree.
 */
import {
  isSkippedMemberError,
  isStrandedRetryRow,
  retryEligibleCount,
  retryNeedsConfirm,
  shouldPoll,
} from './dispatch-inspector-logic';
import {
  REP_STATUS_BADGE,
  classifyRepStatus,
  countByRepStatus,
  rowsNeedingRecheck,
  type RepStatusView,
} from './dispatch-rep-status';
import {
  SEND_OUTCOME_KEYS,
  type PlaybooksDispatchLogRow,
  type PlaybooksSendOutcomes,
  type PlaybooksSendSummary,
} from './schemas';

// ── Send outcomes (plan 2026-10-14-002 U0e (d)) ──────────────────────────────

export type SendOutcomeKey = (typeof SEND_OUTCOME_KEYS)[number];

/**
 * The ONE bucket a person of a send is in — read off the classifier's own
 * view, never a second reading of the row, so the summary, the chips and the
 * Retry button cannot disagree. See `schemas.ts#SEND_OUTCOME_KEYS`.
 */
export function sendOutcomeOf(row: PlaybooksDispatchLogRow, repView: RepStatusView): SendOutcomeKey {
  switch (repView.status) {
    case 'in_progress':
      return (row.retry_count ?? 0) > 0 ? 'retrying' : 'inProgress';
    case 'delivered':
      return row.is_test === true ? 'testOk' : 'delivered';
    case 'not_sent':
      return row.result === 'failed' && isSkippedMemberError(row.error) ? 'heldBackByTerra' : 'stoppedByClay';
    case 'failed_to_start':
    case 'crashed':
      return repView.retryable ? 'failedRetryable' : 'failedFinal';
  }
}

export function countSendOutcomes(
  entries: ReadonlyArray<{ row: PlaybooksDispatchLogRow; view: RepStatusView }>,
): PlaybooksSendOutcomes {
  const counts = Object.fromEntries(SEND_OUTCOME_KEYS.map((key) => [key, 0])) as Record<SendOutcomeKey, number>;
  for (const entry of entries) counts[sendOutcomeOf(entry.row, entry.view)] += 1;
  return counts;
}

/**
 * The send's reconciliation: the buckets must add up to every person the send
 * has. `expected` is counted independently of the classified rows (the
 * summary aggregate's `count(*)`), so a read that lost rows shows up here.
 */
export function reconcileSendOutcomes(
  outcomes: PlaybooksSendOutcomes,
  expected: number,
): { ok: boolean; sum: number; expected: number } {
  const sum = SEND_OUTCOME_KEYS.reduce((n, key) => n + outcomes[key], 0);
  return { ok: sum === expected, sum, expected };
}

// ── One send's summary (plan 2026-10-14-002 U0b(d), U0a) ─────────────────────

/** What the SQL aggregate knows about a send that the classified rows cannot. */
export interface SendAggregateCounts {
  /** Rows still `queued` with no stored job. */
  notBuilt: number;
  /** Rows a dispatch run has picked up (`processing_at` set). */
  handedToClay: number;
}

/**
 * One send's {@link PlaybooksSendSummary} from its rows — the ONE place the
 * summary is computed, so the server (`send-history.ts#summarizeSend`) and the tests
 * that fake a send for the Dispatch log cannot disagree. `finished` is the
 * background build's stamp (a send from before it existed has none and nothing
 * left to build, so `notBuilt === 0` finishes it too).
 *
 * `live`, `retryable`, `retryableCrashes` and `needsRecheck` exist because the
 * client no longer holds every row (the log is paged): each is the SAME rule
 * the row-level buttons and the old whole-array poll used — `shouldPoll`
 * (with its age windows, so a stuck row stops being worth polling; it also
 * stays live up to 20 min while Terra's own triage decides about a
 * network-error crash, and 5 min while that crash waits for a triage to claim
 * it — second review-fix pass U3),
 * `retryEligibleCount`, `retryNeedsConfirm` (a real send's stored-job retries), and
 * `rowsNeedingRecheck`.
 */
export function summarizeSendRows(
  log: readonly PlaybooksDispatchLogRow[],
  counts: SendAggregateCounts,
  finishedStamped: boolean,
  nowMs: number,
): PlaybooksSendSummary {
  const entries = log.map((row) => ({ row, view: classifyRepStatus(row, nowMs) }));
  const byStatus = countByRepStatus(entries.map((entry) => entry.view));
  const finished = finishedStamped || counts.notBuilt === 0;
  return {
    total: log.length,
    built: Math.max(log.length - counts.notBuilt, 0),
    handedToClay: counts.handedToClay,
    inFlight: byStatus.in_progress,
    finished,
    byStatus,
    outcomes: countSendOutcomes(entries),
    live: !finished || shouldPoll(log, nowMs),
    retryable: retryEligibleCount(log),
    // Wire name kept; the meaning widened in PR 1 U2 to every retry that
    // resends a stored job Terra cannot prove undelivered (network crashes AND
    // general Clay failures) on a REAL send — see `retryNeedsConfirm`.
    retryableCrashes: log.filter(retryNeedsConfirm).length,
    // A stranded retry (U6) counts too: the inspector's automatic re-check on
    // open is what reaches the endpoint's stranded-retry recovery.
    needsRecheck: rowsNeedingRecheck(log, nowMs).length + log.filter((row) => isStrandedRetryRow(row, nowMs)).length,
  };
}

// ── What the Dispatch log shows from a summary (plan 2026-10-14-002 U0a) ─────

/** `1,200` — thousands separators, for a send big enough to need them. */
function formatCount(n: number): string {
  return n.toLocaleString('en-US');
}

/**
 * The running line over a send that is still going — "Built 1,200 · with Clay
 * 1,150 · confirmed 900 of 3,000". `confirmed` is the people Clay has answered
 * for (delivered, a finished test, or a stop Clay decided on): a person Terra
 * held back never goes to Clay, and a failure is not a confirmation. `null`
 * once the build is done and nobody is in flight — the badges say the rest.
 */
export function sendProgressLine(summary: PlaybooksSendSummary): string | null {
  if (summary.finished && summary.inFlight === 0) return null;
  const { outcomes } = summary;
  const confirmed = outcomes.delivered + outcomes.testOk + outcomes.stoppedByClay;
  return `Built ${formatCount(summary.built)} · with Clay ${formatCount(summary.handedToClay)} · confirmed ${formatCount(confirmed)} of ${formatCount(summary.total)}`;
}

/** Glaze `StatusBadge` tone per outcome bucket — the same tones the one-status badges use. */
export const SEND_OUTCOME_BADGE: Record<SendOutcomeKey, 'neutral' | 'action' | 'success' | 'warning' | 'error'> = {
  delivered: REP_STATUS_BADGE.delivered,
  testOk: REP_STATUS_BADGE.delivered,
  stoppedByClay: REP_STATUS_BADGE.not_sent,
  heldBackByTerra: 'warning',
  failedRetryable: REP_STATUS_BADGE.failed_to_start,
  failedFinal: REP_STATUS_BADGE.crashed,
  inProgress: REP_STATUS_BADGE.in_progress,
  retrying: REP_STATUS_BADGE.in_progress,
};

/** What a rep reads after the count, per outcome bucket. */
const SEND_OUTCOME_WORDS: Record<SendOutcomeKey, string> = {
  delivered: 'delivered',
  testOk: 'finished',
  stoppedByClay: 'stopped by Clay',
  heldBackByTerra: 'held back',
  failedRetryable: 'failed, retry available',
  failedFinal: 'failed',
  inProgress: 'in progress',
  retrying: 'retrying',
};

/** One badge per outcome bucket someone is in, in `SEND_OUTCOME_KEYS` order. */
export function sendOutcomeBadges(
  outcomes: PlaybooksSendOutcomes,
): { key: SendOutcomeKey; text: string; status: (typeof SEND_OUTCOME_BADGE)[SendOutcomeKey] }[] {
  return SEND_OUTCOME_KEYS.filter((key) => outcomes[key] > 0).map((key) => ({
    key,
    text: `${formatCount(outcomes[key])} ${SEND_OUTCOME_WORDS[key]}`,
    status: SEND_OUTCOME_BADGE[key],
  }));
}

// ── How a send names its people (plan 2026-10-15-005) ────────────────────────

/**
 * "100 of 571 people" for a test that picked a sample of what the play routed
 * (`sampledFrom`, from the receipt or `testSends[].routedCount`), else "N person/people".
 */
export function sendPeopleLabel(total: number, sampledFrom: number | null): string {
  const count = total.toLocaleString('en-US');
  if (sampledFrom !== null) return `${count} of ${sampledFrom.toLocaleString('en-US')} people`;
  return `${count} ${total === 1 ? 'person' : 'people'}`;
}
